import { useEffect, useRef, useState } from "react";

/**
 * Removes solid black matte connected to image edges (phone-on-black exports),
 * keeps dark pixels inside the UI intact.
 */
export default function PunchMatteImage({
  src,
  alt = "",
  className = "",
  threshold = 22,
}) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;
      const isMatte = (i) =>
        d[i] <= threshold &&
        d[i + 1] <= threshold &&
        d[i + 2] <= threshold &&
        d[i + 3] > 8;

      const seen = new Uint8Array(w * h);
      const stack = [];
      const enqueue = (x, y) => {
        if (x < 0 || y < 0 || x >= w || y >= h) return;
        const p = y * w + x;
        if (seen[p]) return;
        seen[p] = 1;
        if (isMatte(p * 4)) stack.push(p);
      };

      for (let x = 0; x < w; x += 1) {
        enqueue(x, 0);
        enqueue(x, h - 1);
      }
      for (let y = 0; y < h; y += 1) {
        enqueue(0, y);
        enqueue(w - 1, y);
      }

      while (stack.length) {
        const p = stack.pop();
        d[p * 4 + 3] = 0;
        const x = p % w;
        const y = (p / w) | 0;
        enqueue(x + 1, y);
        enqueue(x - 1, y);
        enqueue(x, y + 1);
        enqueue(x, y - 1);
      }

      ctx.putImageData(imageData, 0, 0);
      setReady(true);
    };
    img.onerror = () => setReady(true);
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, threshold]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      style={{ opacity: ready ? 1 : 0 }}
    />
  );
}
